import type { ComponentChildren } from "preact";
import { createContext } from "preact";
import { useContext, useEffect, useMemo, useRef, useState } from "preact/hooks";
import type { PictureConfiguration } from "@brickme/project-core/src/model/picture.ts";
import roundAndConstrainImageZoomOffset from "@brickme/project-core/src/utils/round-and-constrain-image-zoom-offset.ts";
import type { GenericSize } from "@brickme/project-core/src/model/geom.ts";
import { isEqual } from "@brickme/project-core/src/utils/unknown.ts";
import { debounce } from "@brickme/project-core/src/utils/function.ts";

type PicturePatch = Partial<
	Omit<PictureConfiguration, "updatedAt" | "numberOfBricks">
>;

type PictureEditValue = {
	readonly picture: PictureConfiguration;
	readonly resetPicture: () => void;
	readonly setPicture: (picture: PictureConfiguration) => void;
	readonly patchPicture: (patch: PicturePatch) => void;
	readonly patchPictureDebounced: (patch: PicturePatch) => void;
};

const pictureContext = createContext<PictureEditValue | undefined>(undefined);

type PictureStorage = {
	readonly setPicture: (
		picture:
			| PictureConfiguration
			| ((previous: PictureConfiguration) => PictureConfiguration),
	) => void;
	readonly getPicture: () => PictureConfiguration;
	readonly addEventListener: (event: "change", handler: () => void) => void;
	readonly removeEventListener: (event: "change", handler: () => void) => void;
};

type PictureEditProviderProps = {
	readonly sourceImageDimensions: GenericSize<number>;
	readonly children: ComponentChildren;
	readonly storage: PictureStorage;
	readonly createDefaultPictureConfiguration: () => PictureConfiguration;
};

function PictureEditProvider({
	sourceImageDimensions,
	children,
	storage,
	createDefaultPictureConfiguration,
}: PictureEditProviderProps) {
	const createDefaultPictureConfigurationRef = useRef(
		createDefaultPictureConfiguration,
	);
	createDefaultPictureConfigurationRef.current =
		createDefaultPictureConfiguration;

	// To allow value useMemo to re-run
	const [pictureVersion, setPictureVersion] = useState(0);
	useEffect(() => {
		function onStorageChange() {
			setPictureVersion((p) => p + 1);
		}
		storage.addEventListener("change", onStorageChange);
		return () => {
			storage.removeEventListener("change", onStorageChange);
		};
	}, [storage]);

	const value = useMemo((): PictureEditValue => {
		function patchPicture(patch: PicturePatch) {
			storage.setPicture((p) => {
				const picture1 = {
					...p,
					...patch,
				};
				const picture2 = {
					...picture1,
					numberOfBricks: {
						width: p.basePlateSize * picture1.numberOfBasePlates.width,
						height: p.basePlateSize * picture1.numberOfBasePlates.height,
					},
				};
				const picture3 = {
					...picture2,
					// If changing numberOfBasePlates or baseplateSize then this can become invalid
					imageZoomOffset: roundAndConstrainImageZoomOffset(
						sourceImageDimensions,
						picture2,
						picture2.imageZoomOffset,
					),
				};
				if (isEqual(picture3, p)) {
					return p;
				}
				return picture3;
			});
		}
		return {
			picture: storage.getPicture(),
			patchPicture,
			setPicture: storage.setPicture,
			patchPictureDebounced: debounce(patchPicture, 50, {
				leading: true,
				trailing: true,
			}),
			resetPicture() {
				storage.setPicture(createDefaultPictureConfigurationRef.current());
			},
		};
	}, [
		sourceImageDimensions,
		storage,
		// Allows re-evaluating value (storage.getPicture)
		pictureVersion,
	]);

	return (
		<pictureContext.Provider value={value}>{children}</pictureContext.Provider>
	);
}

function usePicture() {
	const context = useContext(pictureContext);
	if (context === undefined) {
		throw new Error("usePicture must be used within a PictureProvider");
	}
	return context;
}

export { usePicture, PictureEditProvider };
